import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import FormatPaintOutlinedIcon from '@mui/icons-material/FormatPaintOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ReviewsOutlinedIcon from '@mui/icons-material/ReviewsOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PlumbingOutlinedIcon from '@mui/icons-material/PlumbingOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import * as actions from '../../Store/Actions';
import './Landing.css';

const serviceCategories = [
  { icon: <PlumbingOutlinedIcon />, title: 'Plomberie', text: 'Fuite, robinetterie et petites réparations.' },
  { icon: <ElectricalServicesOutlinedIcon />, title: 'Électricité', text: 'Prises, éclairage et diagnostics simples.' },
  { icon: <ConstructionOutlinedIcon />, title: 'Réparation', text: 'Interventions et remise en état du quotidien.' },
  { icon: <FormatPaintOutlinedIcon />, title: 'Peinture', text: 'Rafraîchissement, retouches et finitions.' },
  { icon: <HandymanOutlinedIcon />, title: 'Montage', text: 'Meubles, étagères et équipements domestiques.' },
  { icon: <CleaningServicesOutlinedIcon />, title: 'Entretien', text: 'Petits travaux d’entretien et de maintenance.' },
];

const steps = [
  {
    number: '01',
    title: 'Décrivez votre besoin',
    text: 'Ajoutez les informations utiles, votre localisation et quelques photos.',
  },
  {
    number: '02',
    title: 'Comparez les propositions',
    text: 'Échangez avec les artisans disponibles et consultez leur estimation.',
  },
  {
    number: '03',
    title: 'Suivez votre intervention',
    text: 'Validez le contrat, suivez l’avancement et évaluez le service réalisé.',
  },
];

function Landing({ logout, userType }) {
  const history = useHistory();
  const year = new Date().getFullYear();

  const goToClientSpace = () => {
    history.push(userType === 'Client' ? '/demands' : '/signup');
  };

  const goToDiscover = () => history.push('/discover');

  const goToArtisanSpace = async () => {
    if (userType === 'Artisan') {
      history.push('/newjobs');
      return;
    }

    if (userType === 'Client') {
      await logout();
    }
    history.push('/registerartisan');
  };

  return (
    <div className="landing-page">
      <section className="landing-hero" aria-labelledby="landing-title">
        <div className="landing-shell landing-hero__grid">
          <div className="landing-hero__content">
            <span className="landing-eyebrow">La mise en relation locale, simplement</span>
            <h1 id="landing-title">Le bon artisan, sans perdre votre journée.</h1>
            <p className="landing-hero__lead">
              Publiez votre petit boulot, recevez des propositions et suivez votre projet
              depuis un espace unique — du premier contact jusqu’à l’évaluation finale.
            </p>
            <div className="landing-actions">
              <button className="landing-button landing-button--primary" onClick={goToClientSpace}>
                {userType === 'Client' ? 'Voir mes demandes' : 'Publier une demande'}
                <ArrowForwardRoundedIcon />
              </button>
              <button className="landing-button landing-button--secondary" onClick={goToArtisanSpace}>
                {userType === 'Artisan' ? 'Trouver une mission' : 'Je suis artisan'}
              </button>
              <button className="landing-button landing-button--ghost" onClick={goToDiscover}>
                Voir autour de moi
              </button>
            </div>
            <ul className="landing-reassurance" aria-label="Avantages du service">
              <li><CheckCircleRoundedIcon /> Demande gratuite</li>
              <li><CheckCircleRoundedIcon /> Suivi en ligne</li>
              <li><CheckCircleRoundedIcon /> Service de proximité</li>
            </ul>
          </div>

          <div className="landing-hero__visual" aria-label="Artisan intervenant chez un particulier">
            <img src="/images/landing-home-image.jpg" alt="Un artisan réalisant une intervention à domicile" />
            <div className="landing-floating-card landing-floating-card--top">
              <VerifiedUserOutlinedIcon />
              <div>
                <strong>Profils contrôlés</strong>
                <span>Accès artisan après validation</span>
              </div>
            </div>
            <div className="landing-floating-card landing-floating-card--bottom">
              <LocationOnOutlinedIcon />
              <div>
                <strong>Au plus près du besoin</strong>
                <span>Recherche par métier et région</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section landing-section--live" aria-labelledby="live-title">
        <div className="landing-shell landing-live">
          <div className="landing-section__heading landing-section__heading--left">
            <span className="landing-eyebrow">Avant de créer un compte</span>
            <h2 id="live-title">Voyez les artisans fiables et les travaux vérifiés autour de vous.</h2>
          </div>
          <div className="landing-live__cards">
            <article><MapOutlinedIcon /><div><h3>Carte locale</h3><p>Profils et réalisations avec localisation approximative.</p></div></article>
            <article><ReviewsOutlinedIcon /><div><h3>Avis vérifiés</h3><p>Uniquement après un projet payé dans LPB.</p></div></article>
            <article><PaymentsOutlinedIcon /><div><h3>Paiement par étapes</h3><p>Acompte puis solde, avec récapitulatif transparent.</p></div></article>
          </div>
          <button className="landing-button landing-button--primary" onClick={goToDiscover}>Explorer sans compte <ArrowForwardRoundedIcon /></button>
        </div>
      </section>

      <section className="landing-section landing-section--white" aria-labelledby="how-title">
        <div className="landing-shell">
          <div className="landing-section__heading">
            <span className="landing-eyebrow">Comment ça marche</span>
            <h2 id="how-title">Votre projet avance en trois étapes claires.</h2>
            <p>Moins d’appels, moins d’incertitude et un historique accessible à tout moment.</p>
          </div>
          <div className="landing-steps">
            {steps.map((step) => (
              <article className="landing-step" key={step.number}>
                <span className="landing-step__number">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" aria-labelledby="services-title">
        <div className="landing-shell">
          <div className="landing-section__heading landing-section__heading--left">
            <span className="landing-eyebrow">Petits travaux, vraie tranquillité</span>
            <h2 id="services-title">Les besoins du quotidien, réunis au même endroit.</h2>
          </div>
          <div className="landing-services">
            {serviceCategories.map((service) => (
              <article className="landing-service" key={service.title}>
                <span className="landing-service__icon" aria-hidden="true">{service.icon}</span>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-section--dark" aria-labelledby="trust-title">
        <div className="landing-shell landing-trust">
          <div className="landing-trust__content">
            <span className="landing-eyebrow landing-eyebrow--light">Une expérience structurée</span>
            <h2 id="trust-title">Chaque échange important reste rattaché à votre projet.</h2>
            <p>
              Estimation, contrat, statut de l’intervention, paiement et avis sont organisés
              dans un parcours commun pour le client et l’artisan.
            </p>
          </div>
          <div className="landing-trust__cards">
            <article>
              <SecurityOutlinedIcon />
              <div>
                <h3>Accès sécurisé</h3>
                <p>Authentification et espaces distincts selon le rôle.</p>
              </div>
            </article>
            <article>
              <VerifiedUserOutlinedIcon />
              <div>
                <h3>Parcours traçable</h3>
                <p>Des statuts explicites de la demande jusqu’à l’avis.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="landing-section landing-section--white" aria-labelledby="join-title">
        <div className="landing-shell landing-join">
          <div>
            <span className="landing-eyebrow">Deux besoins, une seule plateforme</span>
            <h2 id="join-title">Un projet à réaliser ou du savoir-faire à proposer ?</h2>
          </div>
          <div className="landing-join__actions">
            <button className="landing-button landing-button--primary" onClick={goToClientSpace}>
              Espace client
            </button>
            <button className="landing-button landing-button--secondary" onClick={goToArtisanSpace}>
              Espace artisan
            </button>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-shell landing-footer__grid">
          <div>
            <strong>Le Petit Boulot</strong>
            <p>La plateforme de proximité pour organiser vos petits travaux.</p>
          </div>
          <div>
            <span>Une question ?</span>
            <a href="mailto:support@lepetitboulot.fr">support@lepetitboulot.fr</a>
          </div>
          <nav className="landing-footer__links" aria-label="Informations légales">
            <a href="/terms">Conditions</a>
            <a href="/privacy">Confidentialité</a>
            <a href="/review-policy">Avis vérifiés</a>
            <a href="/fees">Tarifs</a>
            <a href="/ranking">Classement</a>
          </nav>
          <small>© {year} Le Petit Boulot</small>
        </div>
      </footer>
    </div>
  );
}

const mapStateToProps = ({ firebase }) => ({
  userType: firebase.profile && firebase.profile.user_type,
});

const mapDispatchToProps = {
  logout: actions.signOut,
};

export default connect(mapStateToProps, mapDispatchToProps)(Landing);
